import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Box, Typography, TextField, InputAdornment } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CommonSelect from "../../common/CommonSelect";
import { validateGeneralContactField } from "./generalcontactdetails.helper";

const getAvailableContactMethods = (info) => {
  const available = [];
  if ((info.mobileNumber || "").trim()) {
    available.push("Mobile Phone");
  }
  if ((info.alternatePhone || "").trim()) {
    available.push("Alternate Phone");
  }
  return available;
};

// Only phone-based options are shown in the dropdowns
const getPhoneContactMethods = (info) => {
  return getAvailableContactMethods(info);
};

const determinePreferredMethod = (info, availableMethods, currentPreferred) => {
  const priorityOrder = ["Mobile Phone", "Alternate Phone"];
  if (!currentPreferred || !availableMethods.includes(currentPreferred)) {
    for (const method of priorityOrder) {
      if (availableMethods.includes(method)) {
        return method;
      }
    }
    return availableMethods[0] || "Mobile Phone";
  }
  return currentPreferred;
};

const GeneralAndContactDetails = ({
  opportunity,
  data,
  onDataChange,
  onValidationChange,
  showErrors,
}) => {
  // Data extraction prioritizing member object then top-level opportunity fields
  const member = opportunity?.member || {};

  const getField = (keys) => {
    for (const key of keys) {
      if (member[key]) return member[key];
      if (opportunity?.[key]) return opportunity[key];
    }
    return "";
  };

  const fName = getField(["firstName", "FirstName", "first_name"]);
  const lName = getField(["lastName", "LastName", "last_name"]);
  const fullName = getField(["fullName", "memberName", "name"]);

  const splitName = (fullName || "").split(" ");

  const firstName = fName || (splitName.length > 0 ? splitName[0] : "");
  const lastName =
    lName || (splitName.length > 1 ? splitName.slice(1).join(" ") : "");

  const initialMobile = getField([
    "phone",
    "phoneNumber",
    "mobilePhone",
    "preferredPhoneNumber",
  ]);
  const initialAltPhone = getField([
    "alternatePhoneNum",
    "alternatePhoneNumber",
    "altPhone",
    "alternatePhone",
  ]);
  const initialEmail = getField(["email", "emailAddress"]);
  const initialAltEmail = getField([
    "alternateEmailAddress",
    "alternateEmail",
    "altEmail",
  ]);

  const [formData, setFormData] = useState(() => {
    const mobile = data.mobileNumber || initialMobile;
    const altPhone = data.alternatePhone || initialAltPhone;
    const emailVal = data.email || initialEmail;
    const altEmailVal = data.alternateEmail || initialAltEmail;

    const initialInfo = {
      mobileNumber: mobile,
      alternatePhone: altPhone,
      email: emailVal,
      alternateEmail: altEmailVal,
    };

    const available = getAvailableContactMethods(initialInfo);

    let contactMethod = data.contactMethod;
    if (!contactMethod || !available.includes(contactMethod)) {
      contactMethod = determinePreferredMethod(
        initialInfo,
        available,
        contactMethod,
      );
    }

    let refillContactMethod = data.refillContactMethod;
    if (!refillContactMethod || !available.includes(refillContactMethod)) {
      refillContactMethod = determinePreferredMethod(
        initialInfo,
        available,
        refillContactMethod,
      );
    }

    return {
      ...data,
      mobileNumber: mobile,
      alternatePhone: altPhone,
      email: emailVal,
      alternateEmail: altEmailVal,
      contactMethod,
      refillContactMethod,
    };
  });

  // Sync initial data back to parent if it was empty or different
  useEffect(() => {
    const needsSync =
      data.mobileNumber !== formData.mobileNumber ||
      data.alternatePhone !== formData.alternatePhone ||
      data.email !== formData.email ||
      data.alternateEmail !== formData.alternateEmail ||
      data.contactMethod !== formData.contactMethod ||
      data.refillContactMethod !== formData.refillContactMethod;

    if (needsSync && onDataChange) {
      onDataChange(formData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  useEffect(() => {
    const fields = [
      "email",
      "contactMethod",
      "refillContactMethod",
      "alternatePhone",
      "alternateEmail",
    ];
    const initialErrors = { ...errors };
    fields.forEach((field) => {
      const error = validateGeneralContactField(
        field,
        formData[field],
        formData,
      );
      if (error) {
        initialErrors[field] = error;
      } else {
        delete initialErrors[field];
      }
    });
    setErrors(initialErrors);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData.contactMethod,
    formData.refillContactMethod,
    formData.alternatePhone,
    formData.alternateEmail,
    formData.mobileNumber,
    formData.email,
  ]);

  useEffect(() => {
    if (onValidationChange) {
      // Only validate editable fields (alternate contact info)
      const hasErrors = Object.keys(errors).length > 0;
      onValidationChange(!hasErrors);
    }
  }, [errors, onValidationChange]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "alternatePhone") {
      const sanitized = value.replaceAll(/\D/g, "").slice(0, 10);
      setFormData((prev) => {
        const next = { ...prev, [name]: sanitized };
        const available = getAvailableContactMethods(next);
        let contactMethod = next.contactMethod;
        let refillContactMethod = next.refillContactMethod;

        if (!available.includes(contactMethod)) {
          contactMethod = determinePreferredMethod(
            next,
            available,
            contactMethod,
          );
        }
        if (!available.includes(refillContactMethod)) {
          refillContactMethod = determinePreferredMethod(
            next,
            available,
            refillContactMethod,
          );
        }

        const finalNext = {
          ...next,
          contactMethod,
          refillContactMethod,
        };
        onDataChange(finalNext);
        return finalNext;
      });
    } else if (name === "alternateEmail") {
      setFormData((prev) => {
        const next = { ...prev, [name]: value };
        const available = getAvailableContactMethods(next);
        let contactMethod = next.contactMethod;
        let refillContactMethod = next.refillContactMethod;

        if (!available.includes(contactMethod)) {
          contactMethod = determinePreferredMethod(
            next,
            available,
            contactMethod,
          );
        }
        if (!available.includes(refillContactMethod)) {
          refillContactMethod = determinePreferredMethod(
            next,
            available,
            refillContactMethod,
          );
        }

        const finalNext = {
          ...next,
          contactMethod,
          refillContactMethod,
        };
        onDataChange(finalNext);
        return finalNext;
      });
    } else if (name === "email") {
      setFormData((prev) => {
        const next = { ...prev, [name]: value };
        onDataChange(next);
        return next;
      });
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  const getErrorProps = (name) => {
    const isInvalid = !!errors[name];
    const shouldShow = touched[name] || showErrors;
    return {
      error: isInvalid && shouldShow,
      helperText: shouldShow ? errors[name] : "",
    };
  };

  const anchorRefs = {
    contactMethod: useRef(null),
    refillContactMethod: useRef(null),
  };

  const [anchorEls, setAnchorEls] = useState({});

  const handleOpenSelect = (field) => (event) => {
    setAnchorEls((prev) => ({ ...prev, [field]: event.currentTarget }));
  };

  const handleCloseSelect = (field) => () => {
    setAnchorEls((prev) => ({ ...prev, [field]: null }));
  };

  const handleSelection = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      onDataChange(next);
      return next;
    });
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const getWidth = (field) => {
    return anchorRefs[field]?.current?.offsetWidth
      ? `${anchorRefs[field].current.offsetWidth}px`
      : "240px";
  };

  const renderSelectTrigger = (field, value, placeholder) => {
    const isOpen = Boolean(anchorEls[field]);
    return (
      <>
        <Box
          ref={anchorRefs[field]}
          onClick={handleOpenSelect(field)}
          sx={{
            width: "100%",
            height: "45px",
            border: "1px solid",
            borderColor: getErrorProps(field).error ? "#d32f2f" : "#E8E8E9",
            borderRadius: "8px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "0 12px",
            backgroundColor: "#FFFFFF",
            cursor: "pointer",
            "&:hover": {
              backgroundColor: "#F9FAFB",
            },
          }}
        >
          <Typography
            sx={{ fontSize: "14px", color: value ? "#0e1324" : "#999893" }}
          >
            {value || placeholder}
          </Typography>
          <KeyboardArrowDownIcon
            sx={{
              color: getErrorProps(field).error ? "#d32f2f" : "#6B7280",
              fontSize: "20px",
              transition: "transform 0.2s",
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            }}
          />
        </Box>
        {getErrorProps(field).error && (
          <Typography
            sx={{ color: "#d32f2f", fontSize: "0.75rem", mt: 0, ml: 1.75 }}
          >
            {getErrorProps(field).helperText}
          </Typography>
        )}
      </>
    );
  };

  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        borderRadius: "12px",
        p: 2,
        border: "1px solid #E8E8E9",
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      <Typography sx={{ fontWeight: 600, fontSize: "16px", color: "#403C6F" }}>
        General and Contact Details
      </Typography>

      <Box sx={{ display: "flex", gap: 3 }}>
        {/* First Name */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography
            sx={{ fontSize: "13px", color: "#66638C", fontWeight: 500 }}
          >
            First Name<span style={{ color: "#E53935" }}>*</span>
          </Typography>
          <TextField
            variant="outlined"
            size="small"
            value={firstName}
            disabled
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#F2F2F2",
                borderRadius: "8px",
                "& fieldset": { border: "none" },
              },
              "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: "#999893",
                fontSize: "14px",
                py: 1.5,
              },
            }}
          />
        </Box>
        {/* Last Name */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography
            sx={{ fontSize: "13px", color: "#66638C", fontWeight: 500 }}
          >
            Last Name<span style={{ color: "#E53935" }}>*</span>
          </Typography>
          <TextField
            variant="outlined"
            size="small"
            value={lastName}
            disabled
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#F2F2F2",
                borderRadius: "8px",
                "& fieldset": { border: "none" },
              },
              "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: "#999893",
                fontSize: "14px",
                py: 1.5,
              },
            }}
          />
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 3 }}>
        {/* Mobile Number */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography
            sx={{ fontSize: "13px", color: "#66638C", fontWeight: 500 }}
          >
            Mobile Number
          </Typography>
          <TextField
            name="mobileNumber"
            variant="outlined"
            size="small"
            value={formData.mobileNumber}
            disabled
            placeholder="Enter mobile number"
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#F2F2F2",
                borderRadius: "8px",
                "& fieldset": { border: "none" },
              },
              "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: "#999893",
                fontSize: "14px",
                py: 1.5,
              },
            }}
          />
        </Box>
        {/* Alternate Phone */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography
            sx={{ fontSize: "13px", color: "#66638C", fontWeight: 500 }}
          >
            Alternate Phone
            {!formData.mobileNumber && (
              <span style={{ color: "#E53935" }}>*</span>
            )}
          </Typography>
          <TextField
            name="alternatePhone"
            variant="outlined"
            size="small"
            autoComplete="new-password"
            placeholder="Enter alternate phone"
            value={formData.alternatePhone}
            onChange={handleChange}
            onBlur={handleBlur}
            {...getErrorProps("alternatePhone")}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography
                      component="span"
                      sx={{
                        fontSize: "12px",
                        color: "#666",
                        whiteSpace: "nowrap",
                        mr: 0.5,
                      }}
                    >
                      {(formData.alternatePhone || "").length}/10
                    </Typography>
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#FFFFFF",
                borderRadius: "8px",
                "& fieldset": {
                  borderColor: getErrorProps("alternatePhone").error
                    ? "#d32f2f"
                    : "#E8E8E9",
                },
              },
              "& .MuiInputBase-input": {
                fontSize: "14px",
                py: 1.5,
                color: "#0e1324",
                pr: 0,
              },
            }}
          />
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 3 }}>
        {/* Email */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography
            sx={{ fontSize: "13px", color: "#66638C", fontWeight: 500 }}
          >
            Email<span style={{ color: "#E53935" }}>*</span>
          </Typography>
          <TextField
            name="email"
            variant="outlined"
            size="small"
            value={formData.email}
            placeholder="Enter email address"
            onChange={handleChange}
            onBlur={handleBlur}
            {...getErrorProps("email")}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#FFFFFF",
                borderRadius: "8px",
                "& fieldset": {
                  borderColor: getErrorProps("email").error
                    ? "#d32f2f"
                    : "#E8E8E9",
                },
              },
              "& .MuiInputBase-input": {
                fontSize: "14px",
                py: 1.5,
                color: "#0e1324",
                pr: 0,
              },
            }}
          />
        </Box>
        {/* Alternate Email */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography
            sx={{ fontSize: "13px", color: "#66638C", fontWeight: 500 }}
          >
            Alternate Email
            {!formData.email && <span style={{ color: "#E53935" }}>*</span>}
          </Typography>
          <TextField
            name="alternateEmail"
            variant="outlined"
            size="small"
            autoComplete="new-password"
            placeholder="Enter alternate email"
            value={formData.alternateEmail}
            onChange={(e) => {
              if (e.target.value.length <= 74) handleChange(e);
            }}
            onBlur={handleBlur}
            {...getErrorProps("alternateEmail")}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography
                      component="span"
                      sx={{
                        fontSize: "12px",
                        color: "#666",
                        whiteSpace: "nowrap",
                        mr: 0.5,
                      }}
                    >
                      {(formData.alternateEmail || "").length}/74
                    </Typography>
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#FFFFFF",
                borderRadius: "8px",
                "& fieldset": {
                  borderColor: getErrorProps("alternateEmail").error
                    ? "#d32f2f"
                    : "#E8E8E9",
                },
              },
              "& .MuiInputBase-input": {
                fontSize: "14px",
                py: 1.5,
                color: "#0e1324",
                pr: 0,
              },
            }}
          />
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 3 }}>
        {/* Contact Method */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography
            sx={{ fontSize: "13px", color: "#66638C", fontWeight: 500 }}
          >
            How would the customer prefer to be contacted?{" "}
            <span style={{ color: "#E53935" }}>*</span>
          </Typography>
          {renderSelectTrigger(
            "contactMethod",
            formData.contactMethod,
            "Select",
          )}
          <CommonSelect
            id="contact-method-select"
            open={Boolean(anchorEls.contactMethod)}
            anchorEl={anchorEls.contactMethod}
            onClose={handleCloseSelect("contactMethod")}
            noteTypes={getPhoneContactMethods(formData)}
            selectedTypes={
              formData.contactMethod ? [formData.contactMethod] : []
            }
            handleTypeToggle={(type) => {
              handleSelection("contactMethod", type);
              handleCloseSelect("contactMethod")();
            }}
            width={getWidth("contactMethod")}
          />
        </Box>
        {/* Auto-Refill Contact Method */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography
            sx={{ fontSize: "13px", color: "#66638C", fontWeight: 500 }}
          >
            When we auto-refill orders,how would they prefer to be contacted?{" "}
            <span style={{ color: "#E53935" }}>*</span>
          </Typography>
          {renderSelectTrigger(
            "refillContactMethod",
            formData.refillContactMethod,
            "Select",
          )}
          <CommonSelect
            id="refill-contact-method-select"
            open={Boolean(anchorEls.refillContactMethod)}
            anchorEl={anchorEls.refillContactMethod}
            onClose={handleCloseSelect("refillContactMethod")}
            noteTypes={getPhoneContactMethods(formData)}
            selectedTypes={
              formData.refillContactMethod ? [formData.refillContactMethod] : []
            }
            handleTypeToggle={(type) => {
              handleSelection("refillContactMethod", type);
              handleCloseSelect("refillContactMethod")();
            }}
            width={getWidth("refillContactMethod")}
          />
        </Box>
      </Box>
    </Box>
  );
};

GeneralAndContactDetails.propTypes = {
  opportunity: PropTypes.object,
  data: PropTypes.object.isRequired,
  onDataChange: PropTypes.func.isRequired,
  onValidationChange: PropTypes.func,
  showErrors: PropTypes.bool,
};

export default GeneralAndContactDetails;
